import { ComputedRef, MaybeRefOrGetter, toValue } from "vue";
import type { TokenInputCaretApi } from "@/src/design_system/form_elements/token_input/composables/useTokenInputCaret";
import {
  DS_TOKEN_INPUT_COMMIT_REASON,
  DS_TOKEN_INPUT_EDIT_MODE,
  DSInputToken,
  DSTokenInputCommitReason,
  DSTokenInputEditMode,
  DSTokenInputEmits,
} from "@/src/design_system/form_elements/token_input/types";

interface UseTokenInputActionsOptions {
  caret: TokenInputCaretApi;
  tokens: MaybeRefOrGetter<DSInputToken[]>;
  draft: MaybeRefOrGetter<string | undefined>;
  isInactive: MaybeRefOrGetter<boolean>;
  commitOnBlur: MaybeRefOrGetter<boolean | undefined>;
  focusDraft: (position?: number | "end") => Promise<void>;
  announceRemoved: (token: DSInputToken) => void;
  expectAddedToken: () => void;
  emit: DSTokenInputEmits;
}

export interface TokenInputActions {
  caret: ComputedRef<number>;
  selectedIndex: ComputedRef<number | null>;
  select: (index: number | null) => void;
  moveTo: (index: number) => void;
  moveCaret: (index: number, draftCaret?: number) => Promise<void>;
  remove: (index: number) => void;
  edit: (index: number) => void;
  open: (index: number, event: MouseEvent) => void;
  commitDraft: (reason: DSTokenInputCommitReason, key?: string) => void;
  commitDraftOnBlur: () => void;
}

type OpenAction = (token: DSInputToken, index: number, event: MouseEvent) => void;

export const useTokenInputActions = ({
  caret,
  tokens,
  draft,
  isInactive,
  commitOnBlur,
  focusDraft,
  announceRemoved,
  expectAddedToken,
  emit,
}: UseTokenInputActionsOptions): TokenInputActions => {
  const activeToken = (index: number): DSInputToken | undefined =>
    toValue(isInactive) ? undefined : toValue(tokens)[index];

  const moveCaret = (index: number, draftCaret?: number): Promise<void> => {
    caret.moveTo(index);

    return focusDraft(draftCaret);
  };

  const remove = (index: number): void => {
    const token = activeToken(index);

    if (!token) {
      return;
    }

    announceRemoved(token);
    caret.remove(index);
    void focusDraft();
  };

  const edit = (index: number): void => {
    if (!toValue(draft)) {
      caret.edit(index);
    }

    void focusDraft("end");
  };

  const selectAndFocus = (index: number): void => {
    if (!toValue(draft)) {
      caret.select(index);
    }

    void focusDraft();
  };

  const openActions: Record<DSTokenInputEditMode, OpenAction> = {
    [DS_TOKEN_INPUT_EDIT_MODE.Text]: (_token, index) => edit(index),
    [DS_TOKEN_INPUT_EDIT_MODE.Replace]: (_token, index) => selectAndFocus(index),
    [DS_TOKEN_INPUT_EDIT_MODE.None]: (token, index, event) => emit("token:click", { token, index }, event),
  };

  const open = (index: number, event: MouseEvent): void => {
    const token = activeToken(index);

    if (token) {
      openActions[token.editMode ?? DS_TOKEN_INPUT_EDIT_MODE.Text](token, index, event);
    }
  };

  const commitDraft = (reason: DSTokenInputCommitReason, key?: string): void => {
    const text = toValue(draft) ?? "";

    expectAddedToken();
    emit("commit", key === undefined ? { draft: text, reason } : { draft: text, reason, key });
  };

  const commitDraftOnBlur = (): void => {
    if (toValue(commitOnBlur) && toValue(draft) && !toValue(isInactive)) {
      commitDraft(DS_TOKEN_INPUT_COMMIT_REASON.Blur);
    }
  };

  return {
    caret: caret.caret,
    selectedIndex: caret.selectedIndex,
    select: caret.select,
    moveTo: caret.moveTo,
    moveCaret,
    remove,
    edit,
    open,
    commitDraft,
    commitDraftOnBlur,
  };
};
